import AsyncStorage from '@react-native-async-storage/async-storage';
import { rachaService } from '@/services/rachaService';
import { Racha, FirebaseOperationResult } from '@/types/app';
import { logger } from './logger';

export interface MigrationProgress {
  total: number;
  completed: number;
  current: string;
  errors: string[];
}

export const migrateData = {
  // Verificar se usuário tem dados locais para migrar
  async hasLocalData(userId: string): Promise<boolean> {
    try {
      const storedRachas = await AsyncStorage.getItem(`@rachas_${userId}`);
      return storedRachas !== null && JSON.parse(storedRachas).length > 0;
    } catch (error) {
      console.error('Erro ao verificar dados locais:', error);
      return false;
    }
  },

  // Carregar dados do AsyncStorage
  async loadLocalData(userId: string): Promise<Racha[]> {
    try {
      const storedRachas = await AsyncStorage.getItem(`@rachas_${userId}`);
      if (!storedRachas) return [];

      const parsedRachas = JSON.parse(storedRachas).map((racha: any) => ({
        ...racha,
        status: racha.status || 'ativo',
        dataFinalizacao: racha.dataFinalizacao ? new Date(racha.dataFinalizacao) : undefined,
        despesas: racha.despesas.map((despesa: any) => ({
          ...despesa,
          data: new Date(despesa.data)
        })),
        gruposDeBolso: racha.gruposDeBolso || [],
        dataCriacao: new Date(), // Adicionar data de criação
        dataAtualizacao: new Date() // Adicionar data de atualização
      }));

      return parsedRachas;
    } catch (error) {
      console.error('Erro ao carregar dados locais:', error);
      return [];
    }
  },

  // Migrar um racha específico
  async migrateRacha(racha: Racha): Promise<FirebaseOperationResult> {
    try {
      // Validar dados do racha antes da migração
      if (!racha.nome || !racha.criadorId) {
        return { 
          success: false, 
          error: 'Dados do racha inválidos: nome ou criadorId ausente' 
        };
      }

      // Verificar se o usuário já tem rachas no Firestore com o mesmo nome
      try {
        const existingRachas = await rachaService.getUserRachas(racha.criadorId);
        if (existingRachas.success && existingRachas.data) {
          const duplicateRacha = existingRachas.data.find((r: Racha) => 
            r.nome === racha.nome && 
            r.criadorId === racha.criadorId
          );
          
          if (duplicateRacha) {
            console.log(`Racha "${racha.nome}" já existe no Firestore, pulando migração`);
            return { success: true, data: { id: duplicateRacha.id, skipped: true } };
          }
        }
      } catch (permissionError) {
        logger.warn('Error checking existing rachas (possible permission issue)', { permissionError });
        // Continuar com a migração mesmo se não conseguir verificar duplicatas
      }

      // Preparar dados do racha para migração com validação
      const rachaData = {
        nome: racha.nome.trim(),
        emoji: racha.emoji || '💰',
        criadorId: racha.criadorId,
        participantes: racha.participantes || [],
        despesas: (racha.despesas || []).map(despesa => ({
          ...despesa,
          data: despesa.data instanceof Date ? despesa.data : new Date(despesa.data)
        })),
        ultimaAtividade: racha.ultimaAtividade || 'Migrado',
        status: racha.status || 'ativo',
        dataFinalizacao: racha.dataFinalizacao,
        gruposDeBolso: racha.gruposDeBolso || []
      };

      // Criar racha no Firestore com retry em caso de erro temporário
      let result;
      let retryCount = 0;
      const maxRetries = 3;

      while (retryCount < maxRetries) {
        try {
          result = await rachaService.createRacha(rachaData);
          break;
        } catch (error: any) {
          retryCount++;
          
          // Se for erro de permissão, não tentar novamente
          if (error?.code === 'permission-denied' || 
              error?.message?.includes('Missing or insufficient permissions')) {
            return { 
              success: false, 
              error: `Erro de permissão: Verifique se você tem acesso ao Firestore. ${error.message}` 
            };
          }
          
          // Se for erro de rede, tentar novamente após delay
          if (retryCount < maxRetries && (
              error?.code === 'unavailable' || 
              error?.message?.includes('network') ||
              error?.message?.includes('timeout')
            )) {
            logger.warn(`Attempt ${retryCount} failed, retrying in 2s...`);
            await new Promise(resolve => setTimeout(resolve, 2000));
            continue;
          }
          
          throw error;
        }
      }
      
      if (result?.success) {
        logger.info(`Racha "${racha.nome}" migrated successfully`);
        return { success: true, data: result.data };
      } else {
        logger.error(`Error migrating racha "${racha.nome}"`, { error: result?.error });
        return result || { success: false, error: 'Erro desconhecido na migração' };
      }
    } catch (error: any) {
      logger.error(`Error migrating racha "${racha.nome}"`, { error });
      
      // Tratamento específico para diferentes tipos de erro
      let errorMessage = 'Erro desconhecido';
      
      if (error?.code === 'permission-denied') {
        errorMessage = 'Erro de permissão: Verifique se você tem acesso ao Firestore';
      } else if (error?.code === 'unavailable') {
        errorMessage = 'Serviço temporariamente indisponível. Tente novamente em alguns minutos';
      } else if (error?.code === 'unauthenticated') {
        errorMessage = 'Usuário não autenticado. Faça login novamente';
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      return { 
        success: false, 
        error: errorMessage
      };
    }
  },

  // Migrar todos os dados do usuário
  async migrateAllData(
    userId: string, 
    onProgress?: (progress: MigrationProgress) => void
  ): Promise<FirebaseOperationResult> {
    try {
      // Carregar dados locais
      const localRachas = await this.loadLocalData(userId);
      
      if (localRachas.length === 0) {
        return { success: true, data: { message: 'Nenhum dado para migrar' } };
      }

      const progress: MigrationProgress = {
        total: localRachas.length,
        completed: 0,
        current: '',
        errors: []
      };

      const migratedRachas: string[] = [];

      // Migrar cada racha
      for (const racha of localRachas) {
        progress.current = racha.nome;
        onProgress?.(progress);

        const result = await this.migrateRacha(racha);
        
        if (result.success) {
          migratedRachas.push(racha.id);
          progress.completed++;
        } else {
          progress.errors.push(`${racha.nome}: ${result.error}`);
        }

        onProgress?.(progress);
      }

      // Verificar se houve erros
      if (progress.errors.length > 0) {
        logger.warn('Migration completed with errors', { errors: progress.errors });
        return { 
          success: false, 
          error: `Migração parcial: ${progress.errors.length} erros`,
          data: { migratedRachas, errors: progress.errors }
        };
      }

      logger.info('Migration completed successfully');
      return { 
        success: true, 
        data: { 
          message: `${migratedRachas.length} rachas migrados com sucesso`,
          migratedRachas 
        }
      };
    } catch (error) {
      logger.error('Error in migration', { error });
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Erro desconhecido' 
      };
    }
  },

  // Fazer backup dos dados locais antes da migração
  async backupLocalData(userId: string): Promise<FirebaseOperationResult> {
    try {
      const storedRachas = await AsyncStorage.getItem(`@rachas_${userId}`);
      if (!storedRachas) {
        return { success: true, data: { message: 'Nenhum dado para backup' } };
      }

      const backupKey = `@rachas_backup_${userId}_${Date.now()}`;
      await AsyncStorage.setItem(backupKey, storedRachas);

      return { 
        success: true, 
        data: { 
          message: 'Backup criado com sucesso',
          backupKey 
        }
      };
    } catch (error) {
      logger.error('Error creating backup', { error });
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Erro desconhecido' 
      };
    }
  },

  // Limpar dados locais após migração bem-sucedida
  async clearLocalData(userId: string): Promise<FirebaseOperationResult> {
    try {
      await AsyncStorage.removeItem(`@rachas_${userId}`);
      return { success: true, data: { message: 'Dados locais limpos' } };
    } catch (error) {
      logger.error('Error clearing local data', { error });
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Erro desconhecido' 
      };
    }
  },

  // Restaurar dados do backup (em caso de erro)
  async restoreFromBackup(userId: string, backupKey: string): Promise<FirebaseOperationResult> {
    try {
      const backupData = await AsyncStorage.getItem(backupKey);
      if (!backupData) {
        return { success: false, error: 'Backup não encontrado' };
      }

      await AsyncStorage.setItem(`@rachas_${userId}`, backupData);
      return { success: true, data: { message: 'Dados restaurados do backup' } };
    } catch (error) {
      logger.error('Error restoring backup', { error });
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Erro desconhecido' 
      };
    }
  },

  // Processo completo de migração com segurança
  async safeMigration(
    userId: string,
    onProgress?: (progress: MigrationProgress) => void
  ): Promise<FirebaseOperationResult> {
    try {
      // 1. Verificar se há dados para migrar
      const hasData = await this.hasLocalData(userId);
      if (!hasData) {
        return { success: true, data: { message: 'Nenhum dado para migrar' } };
      }

      // 2. Fazer backup dos dados
      const backupResult = await this.backupLocalData(userId);
      if (!backupResult.success) {
        return backupResult;
      }

      const backupKey = backupResult.data?.backupKey;

      // 3. Executar migração
      const migrationResult = await this.migrateAllData(userId, onProgress);

      // 4. Se migração foi bem-sucedida, limpar dados locais
      if (migrationResult.success) {
        await this.clearLocalData(userId);
        
        // Remover backup após sucesso (opcional)
        if (backupKey) {
          await AsyncStorage.removeItem(backupKey);
        }

        return {
          success: true,
          data: {
            message: 'Migração concluída com sucesso',
            ...migrationResult.data
          }
        };
      } else {
        // 5. Se houve erro, manter dados locais e backup
        logger.warn('Migration failed, local data maintained');
        return {
          success: false,
          error: `Migração falhou: ${migrationResult.error}`,
          data: {
            backupKey,
            ...migrationResult.data
          }
        };
      }
    } catch (error) {
      logger.error('Error in safe migration', { error });
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Erro desconhecido' 
      };
    }
  },

  // Verificar se migração já foi executada
  async isMigrationCompleted(userId: string): Promise<boolean> {
    try {
      const migrationFlag = await AsyncStorage.getItem(`@migration_completed_${userId}`);
      return migrationFlag === 'true';
    } catch (error) {
      logger.error('Error checking migration status', { error });
      return false;
    }
  },

  // Marcar migração como concluída
  async markMigrationCompleted(userId: string): Promise<void> {
    try {
      await AsyncStorage.setItem(`@migration_completed_${userId}`, 'true');
    } catch (error) {
      logger.error('Error marking migration as completed', { error });
    }
  }
};
